import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { PasswordInput, TextInput } from "../components/Inputs";
import { useLoaderStore } from "../context";
import useUserStore from "../context/useUserStore";
import { useAlert, useApi } from "../hooks";
import { API } from "../utils/constants";

/**
 * Login — Admin login page for Twin City Silver Bullet.
 *
 * Flow (CON-002 §1):
 * - POST /api/auth/login → { accessToken, mustChangePassword, user }
 * - On success: stores auth state → redirects to /admin (or /change-password)
 * - On 401: shows invalid credentials toast
 * - On 423: shows account locked toast
 */
export default function Login() {
  const alert = useAlert();
  const { post } = useApi();
  const { login } = useUserStore();
  const { setShowLoader } = useLoaderStore();
  const navigate = useNavigate();
  const location = useLocation();

  // The page the user was on before a 401 bounced them here (FE-5). Falls back
  // to /admin. Guard against looping back to auth routes.
  const fromTarget = location?.state?.from;
  const returnTo =
    fromTarget && fromTarget !== "/login" && fromTarget !== "/change-password"
      ? fromTarget
      : "/admin";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const isValid = () => {
    if (!username || !username.trim()) {
      alert("Invalid input", "Please enter your username or email.", "warning");
      return false;
    }
    if (!password || !password.trim()) {
      alert("Invalid input", "Please enter your password.", "warning");
      return false;
    }
    return true;
  };

  const handleSignIn = async () => {
    if (!isValid()) return;
    setShowLoader(true);

    try {
      const res = await post(API.AUTH.LOGIN, { username, password });
      const { user, accessToken, mustChangePassword } = res;

      // Store user with mustChangePassword flag so App.js guard can read it
      login({ ...user, mustChangePassword }, accessToken);

      if (mustChangePassword) {
        navigate("/change-password");
      } else {
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      if (err?.status === 423) {
        alert(
          "Account locked",
          "Too many failed attempts. Please contact your administrator.",
          "error"
        );
      } else {
        alert(
          "Login failed",
          err?.data?.detail || "Invalid username or password.",
          "error"
        );
      }
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cream flex items-center justify-center px-4"
      data-testid="login-page"
    >
      <div className="w-full max-w-sm">
        {/* Brand heading */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl text-charcoal leading-tight">
            Twin City<br />Silver Bullet
          </h1>
          <p className="font-body text-charcoal/60 mt-2 text-sm">Admin Portal</p>
        </div>

        {/* Login card */}
        <div className="bg-white border border-aluminum/30 rounded-sm shadow-sm p-8 space-y-5">
          <h2 className="font-heading text-xl text-charcoal tracking-wider">SIGN IN</h2>

          <div>
            <TextInput
              label="Username or Email"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="username-input"
              onKeyDown={(e) => { if (e.key === "Enter") handleSignIn(); }}
            />
          </div>

          <div>
            <PasswordInput
              label="Password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSignIn(); }}
              data-testid="password-input"
            />
          </div>

          <button
            onClick={handleSignIn}
            className="w-full bg-teal text-white font-heading tracking-widest text-lg py-2.5 rounded-sm hover:bg-teal/80 active:scale-95 transition-all"
            data-testid="sign-in-button"
          >
            SIGN IN
          </button>

          <Link
            to="/forgot-password"
            className="block text-center font-heading tracking-wider text-sm text-teal hover:text-teal/70 transition-colors"
            data-testid="forgot-password-link"
          >
            FORGOT PASSWORD?
          </Link>
        </div>
      </div>
    </div>
  );
}
